import { useState, useRef, useEffect, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Sparkles, Send, RotateCcw, AlertCircle } from 'lucide-react'
import { Drawer, Button, Chip, cn } from '../design'
import { agentChat, getAgentStatus, type AgentChatResponse } from '../api/agent'
import { ApiError } from '../api/client'
import { Link } from 'react-router-dom'

type Turn = { role: 'user' | 'assistant'; text: string; tools?: string[] }

export function AgentChat({
  open,
  onOpenChange,
  projectId,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  projectId?: number
}) {
  const qc = useQueryClient()
  const statusQ = useQuery({ queryKey: ['agent', 'status'], queryFn: getAgentStatus, staleTime: 30_000, enabled: open })

  const [draft, setDraft] = useState('')
  const [turns, setTurns] = useState<Turn[]>([])
  const [sessionId, setSessionId] = useState<string | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [turns.length])

  const chatMut = useMutation({
    mutationFn: (message: string) => agentChat(message, sessionId, projectId),
    onSuccess: (resp: AgentChatResponse) => {
      setSessionId(resp.session_id)
      setTurns(t => [...t, { role: 'assistant', text: resp.message, tools: resp.tool_calls_made }])
      // Tool calls likely mutated stories — refresh background data
      qc.invalidateQueries({ queryKey: ['stories'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      qc.invalidateQueries({ queryKey: ['me'] })
    },
    onError: (err: Error) => {
      setError(err instanceof ApiError ? err.message : err.message)
    },
  })

  function onSend(e: FormEvent) {
    e.preventDefault()
    const text = draft.trim()
    if (!text) return
    setError(null)
    setTurns(t => [...t, { role: 'user', text }])
    chatMut.mutate(text)
    setDraft('')
  }

  function newSession() {
    setSessionId(undefined)
    setTurns([])
    setError(null)
  }

  const connected = statusQ.data?.connected ?? false

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      width="md"
      title={<span className="flex items-center gap-2"><Sparkles size={14} /> Planning Agent</span>}
      description={statusQ.data ? `${statusQ.data.model}` : undefined}
      footer={
        <form onSubmit={onSend} className="flex items-end gap-2">
          <button
            type="button"
            onClick={newSession}
            title="New session"
            className="text-fg-subtle hover:text-fg p-2 rounded hover:bg-bg-muted"
            disabled={turns.length === 0}
          >
            <RotateCcw size={14} />
          </button>
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onSend(e as unknown as FormEvent)
            }}
            placeholder={connected ? 'What should I focus on?' : 'Configure Anthropic key first…'}
            disabled={!connected || chatMut.isPending}
            rows={2}
            className="flex-1 rounded-md border border-border bg-bg-elevated px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent disabled:opacity-50"
          />
          <Button type="submit" leftIcon={<Send size={12} />} disabled={!connected || !draft.trim() || chatMut.isPending}>
            {chatMut.isPending ? '…' : 'Send'}
          </Button>
        </form>
      }
    >
      {!connected && (
        <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-3 text-sm flex items-start gap-2">
          <AlertCircle size={16} className="text-warning shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-fg">Agent not connected</div>
            <p className="text-xs text-fg-muted mt-1">
              Add your Anthropic API key in <Link to="/settings" className="text-accent hover:underline">Settings → Planning Agent</Link>.
            </p>
          </div>
        </div>
      )}

      <div ref={scrollRef} className="space-y-3 min-h-[200px] max-h-[55vh] overflow-y-auto pr-1">
        {connected && turns.length === 0 && (
          <div className="text-xs text-fg-subtle italic mt-2">
            Ask things like:
            <ul className="list-disc ml-5 mt-1 space-y-0.5">
              <li>"What should I work on today?"</li>
              <li>"Summarize Sprint 1 status."</li>
              <li>"Mark ATL-3 as done."</li>
              <li>"Create a story 'Fix navbar bug' in Atlas, high priority, due Friday."</li>
            </ul>
          </div>
        )}

        {turns.map((t, i) => <TurnView key={i} turn={t} />)}

        {chatMut.isPending && (
          <div className="flex items-center gap-2 text-xs text-fg-subtle">
            <span className="h-3 w-3 rounded-full border-2 border-border border-t-accent animate-spin" />
            thinking…
          </div>
        )}

        {error && (
          <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">{error}</div>
        )}
      </div>
    </Drawer>
  )
}

function TurnView({ turn }: { turn: Turn }) {
  const isUser = turn.role === 'user'
  return (
    <div className={cn('rounded-md px-3 py-2 text-sm', isUser ? 'bg-bg-muted ml-8' : 'bg-accent-soft mr-8 border border-accent/30')}>
      <div className="text-[10px] uppercase tracking-wider font-semibold mb-1 text-fg-subtle">
        {isUser ? 'You' : 'Atlas'}
      </div>
      <div className="whitespace-pre-wrap leading-relaxed">{turn.text}</div>
      {!isUser && turn.tools && turn.tools.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {turn.tools.map((t, i) => (
            <Chip key={i} size="sm" tone="info" className="font-mono text-[9px]">{t}</Chip>
          ))}
        </div>
      )}
    </div>
  )
}
